import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import Tooltip from '@/components/platform/ui/Tooltip';

interface MediaValueHeaderSectionProps {
  canEdit: boolean;
}

export function MediaValueHeaderSection({ canEdit }: MediaValueHeaderSectionProps) {
  const { darkMode } = useDarkMode();

  return (
    <div
      className={`grid grid-cols-1 gap-x-8 gap-y-10 border-b pb-12 md:grid-cols-3 ${darkMode ? 'border-white/10' : 'border-gray-900/10'} ${!canEdit ? 'opacity-70' : ''}`}
    >
      <div className="md:col-span-3">
        <h2
          className={`text-lg/7 font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'} flex items-center`}
        >
          Media Value
          <Tooltip
            title="Quantitative Data Benchmarks"
            definition="Industry-standard CPM (Cost Per Mille/Thousand) rates and value ranges for different media types. Use these benchmarks to validate and contextualize your media value calculations."
            assessmentCriteria={[
              'CPM = Cost Per 1,000 impressions/views/engagements',
              'Ranges vary by media type, reach, and market positioning',
              'Use actual market rates when available; otherwise reference benchmarks',
            ]}
            scoringGuidelines={[
              {
                range: 'TV CPM',
                multiplier: '€15-25',
                level: 'Low Range',
                description: 'Regional/smaller broadcasts',
              },
              {
                range: 'TV CPM',
                multiplier: '€25-35',
                level: 'Medium Range',
                description: 'National standard broadcasts',
              },
              {
                range: 'TV CPM',
                multiplier: '€35-50',
                level: 'High Range',
                description: 'Prime time/major events',
              },
              {
                range: 'TV CPM',
                multiplier: '€50-75',
                level: 'Premium Range',
                description: 'Major sports events/finals',
              },
              {
                range: 'Web TV CPM',
                multiplier: '€3-6 (Low) | €6-10 (Med) | €10-15 (High) | €15-25 (Premium)',
                level: '',
                description: '',
              },
              {
                range: 'PR CPM',
                multiplier: '€2-4 (Low) | €4-7 (Med) | €7-12 (High) | €12-20 (Premium)',
                level: '',
                description: '',
              },
              {
                range: 'Digital CPM',
                multiplier: '€1-3 (Low) | €3-6 (Med) | €6-10 (High) | €10-15 (Premium)',
                level: '',
                description: '',
              },
              {
                range: 'Social CPM',
                multiplier: '€3-6 (Low) | €6-10 (Med) | €10-15 (High) | €15-25 (Premium)',
                level: '',
                description: '',
              },
              {
                range: 'Event CPM',
                multiplier: '€5-10 (Low) | €10-15 (Med) | €15-25 (High) | €25-40 (Premium)',
                level: '',
                description: '',
              },
              {
                range: 'Hospitality',
                multiplier:
                  '€100-300 (Low) | €300-600 (Med) | €600-1000 (High) | €1000-2000 (Premium)',
                level: '',
                description: '',
              },
            ]}
            wide={true}
          />
        </h2>
        <p className={`mt-1 text-sm/6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Quantitative media metrics and CPM benchmarks for all media channels.
        </p>
        {!canEdit && (
          <span className="mt-2 inline-block rounded bg-red-500/10 px-2 py-1 text-xs font-medium text-red-500">
            Read Only
          </span>
        )}
      </div>
    </div>
  );
}
